import React from "react";

/**
 * Fallback UI shown when an external website blocks iframe embedding
 * via X-Frame-Options or CSP frame-ancestors headers.
 *
 * Renders identically in every browser so the user always sees the same
 * message regardless of whether Chrome, Firefox, or Safari is being used.
 *
 * Content is always clipped to the container boundaries regardless of
 * how small the iframe element is. Less-critical rows (note, button) are
 * hidden via CSS when vertical space is too tight.
 *
 * This is NOT related to cookie consent — that flow uses CookieBlockedMessage.
 */
const IframeBlockedMessage = ({ style = {}, url = "" }) => {
  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    backgroundColor: "#fff8e1",
    border: "2px solid #ff9800",
    borderRadius: "8px",
    padding: "12px 16px",
    boxSizing: "border-box",
    overflow: "hidden",       // keep everything inside the box
    textAlign: "center",
    color: "#666",
    fontSize: "13px",
    lineHeight: "1.4",
    gap: "6px",               // uniform spacing; no margins on children needed
    ...style,
  };

  return (
    <div
      style={containerStyle}
      title="This website cannot be previewed due to external security restrictions"
    >
      {/* Icon — shrinks but never pushes siblings outside the box */}
      <svg
        style={{ flexShrink: 0 }}
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 9V11M12 15H12.01M5.07 19H18.93C20.06 19 20.91 17.86 20.49 16.82L13.56 3.23C13.15 2.23 11.85 2.23 11.44 3.23L4.51 16.82C4.09 17.86 4.94 19 5.07 19Z"
          stroke="#ff9800"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <div
        style={{
          fontWeight: "600",
          color: "#f57c00",
          fontSize: "13px",
          flexShrink: 0,
          minHeight: 0,
        }}
      >
        Preview Not Available
      </div>

      <div
        style={{
          fontSize: "12px",
          color: "#666",
          flexShrink: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        This website restricts iframe embedding for security reasons.
      </div>

      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-block",
            padding: "4px 12px",
            fontSize: "11px",
            color: "#f57c00",
            border: "1px solid #f57c00",
            borderRadius: "4px",
            textDecoration: "none",
            cursor: "pointer",
            flexShrink: 0,
            minHeight: 0,
            transition: "background-color 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#fff3e0";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          Open in new tab
        </a>
      )}

      <div
        style={{
          fontSize: "11px",
          color: "#999",
          fontStyle: "italic",
          flexShrink: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        The link will work normally when viewed by recipients.
      </div>
    </div>
  );
};

export default IframeBlockedMessage;
